import {
  assignInWith,
  isNaN,
  isUndefined,
  partialRight,
  isBoolean,
  isEmpty,
  sortBy
} from 'lodash';
import moment from 'moment';
import {
  dateFormat_DD_MM_YY,
  dateTimeFormat,
  MIN_FRACTION_DIGITS
} from '../util/GlobalConstant';
import { formatNumeric } from '../util';
import { createAxiosServiceInstance } from './api';

/**
 * extendFromRight
 * @param target
 * @param source
 * @returns {*}
 */
export const extendFromRight = (target, source) => {
  return partialRight(assignInWith, (objVal, srcVal) =>
    isUndefined(objVal) ? srcVal : objVal
  )(target, source);
};

/**
 * getFormattedDate
 * @param dateString
 * @param dateFormat
 * @returns {string}
 */
export const getFormattedDate = (dateString, dateFormat) => {
  return dateString
    ? moment.utc(dateString, [dateTimeFormat]).local().format(dateFormat)
    : '';
};

/**
 * getFormattedDateWithoutTimezone
 * @param dateString
 * @param dateFormat
 * @returns {string}
 */
export const getFormattedDateWithoutTimezone = (dateString, dateFormat) => {
  return dateString
    ? moment.utc(dateString, [dateTimeFormat]).format(dateFormat)
    : '';
};

// Analyst marks it as an 'Issue' on the AMLS UI -  is_str = 1 and is_issue = 1 and investigation_result = 'STR' (Hbase)
// b) Analyst marks it as 'No Issue' on the AMLS UI - is_str = 0 and is_issue = 0 and investigation_result = 'Non-STR' (Hbase)
// c) Analyst marks it as 'Insufficient Information' on the AMLS UI - is_str = 0 and is_issue = 2 and investigation_result = 'Undetermined' (Hbase)
/**
 * getInvestigationResult
 * @param isStr
 * @param isIssue
 * @param investigationResult
 * @returns {string}
 */
export const getInvestigationResult = (
  alertStatus,
  isStr,
  isIssue,
  investigationResult
) => {
  if (alertStatus == 1) {
    if (isStr === 1 && isIssue === 1 && investigationResult === 'STR') {
      return 'Issue';
    } else if (
      isStr === 0 &&
      isIssue === 0 &&
      investigationResult === 'Non-STR'
    ) {
      return 'No Issue';
    } else {
      return 'Insufficient Information';
    }
  }
  return '-';
};

export const getSTR = (alertStatus, isStr) => {
  if (alertStatus == 1) {
    if (isStr === 1) {
      return 'YES';
    }
    return 'NO';
  }
  return '-';
};

/**
 * getAlertStatus
 * @param status
 * @returns {string}
 */
export const getAlertStatus = (status) => {
  switch (status) {
    case 0:
      return 'Open';
    case 1:
      return 'Closed';
    case 2:
      return 'Assigned';
    case 3:
      return 'Under Investigation';
    case 4:
      return 'Pending Approval';
    default:
      return '-';
  }
};

/**
 * isEmptyNullUndef
 * @param str
 * @returns {boolean}
 */
export const isEmptyNullUndef = (str) => {
  return !(str && str !== 'null' && str !== 'undefined');
};

/**
 * getDecimalInPercentage
 * @param num
 * @returns {string|*}
 */
export const getDecimalInPercentage = (num) => {
  if (isNaN(num)) {
    return num;
  } else {
    const newNum = Number(num * 100).toFixed(2);
    if (newNum == 0.0) {
      return `${'0.00'} %`;
    }
    return `${newNum} %`;
  }
};

export const getDecimalToHundredScale = (num) => {
  if (isNaN(num)) {
    return num;
  } else {
    const newNum = Number(Number(num * 100).toFixedNoRounding(2));
    if (newNum === 0) {
      return 0.0;
    }
    return newNum;
  }
};

export const getDecimalWithoutHundredMultiplier = (num) => {
  if (isNaN(num)) {
    return num;
  } else {
    const newNum = Number(Number(num).toFixedNoRounding(2));
    if (newNum === 0) {
      return 0.0;
    }
    return newNum;
  }
};

export const getToHundredScaleWithoutRound = (num) => {
  if (isNaN(num)) {
    return num;
  } else {
    const newNum = Number(num * 100);
    if (String(newNum) === '-0.00') {
      return `${'0'}`;
    }
    return `${newNum}`;
  }
};

Number.prototype.toFixedNoRounding = function (n) {
  const reg = new RegExp('^-?\\d+(?:\\.\\d{0,' + n + '})?', 'g');
  const a = this.toString().match(reg)[0];
  const dot = a.indexOf('.');
  if (dot === -1) {
    // integer, insert decimal dot and pad up zeros
    return a + '.' + '0'.repeat(n);
  }
  const b = n - (a.length - dot) + 1;
  return b > 0 ? a + '0'.repeat(b) : a;
};

/**
 * getInDecimal
 * @param num
 * @returns {string|*}
 */
export const getInDecimal = (num, valueAfterDecimal) => {
  if (isNaN(Number(num))) {
    return num;
  } else {
    const newNum = Number(num).toFixed(
      valueAfterDecimal ? valueAfterDecimal : 2
    );
    if (newNum === '-0.00') {
      return '0.00';
    }
    return `${newNum}`;
  }
};

/**
 * ageFormatter
 * @param val
 * @returns {string}
 */
export const ageFormatter = (val) => {
  if (isEmptyNullUndef(val)) {
    return '-';
  } else {
    const toNumAge = Number(val).toFixed(0);
    if (isNaN(toNumAge)) {
      return '-';
    }
    return `${toNumAge} Yrs`;
  }
};

/**
 * putHyphenIfEmpty
 * @param str
 * @returns {string|*}
 */
export const putHyphenIfEmpty = (str) => {
  if (str && str !== 'null' && str !== 'undefined') {
    return str;
  }
  return '-';
};

/**
 * convertToFormattedNumber
 * @param num
 * @returns {string}
 */
export const convertToFormattedNumber = (num) => {
  if (isEmptyNullUndef(num)) {
    return '-';
  } else if (num === '-0.00' || num === '0.00') {
    return '0.00';
  } else if (isNaN(num) || isNaN(Number(num))) {
    return '-';
  } else {
    return `${formatNumeric(Number(num).toFixed(2))}`;
  }
};

/**
 * convertToFormattedMoney
 * @param num
 * @param fractionDigits
 * @param nanHandler
 * @returns {string}
 */
export const convertToFormattedMoney = (
  num,
  fractionDigits = 2,
  nanHandler = 'empty'
) => {
  if (num === '-0.00' || num === '0.00' || Number(num) === 0.0) {
    if (fractionDigits > MIN_FRACTION_DIGITS) {
      return `$ ${Number(0).toFixed(fractionDigits)}`;
    }
    return `$ ${'0.00'}`;
  } else if (isEmptyNullUndef(num)) {
    return '-';
  } else if (
    String(num).toLowerCase().includes('million') ||
    String(num).toLowerCase().includes('billion') > 0
  ) {
    return `$ ${num}`;
  } else if (isNaN(num) || isNaN(Number(num))) {
    if (nanHandler === 'empty') {
      return '-';
    } else {
      return num;
    }
  } else {
    return `$ ${formatNumeric(Number(num).toFixed(fractionDigits))}`;
  }
};

/**
 * getAlertIdFromURL
 * @returns {string}
 */
export const getAlertIdFromURL = () => {
  const href = location.pathname;
  const parts = href.split('/');
  return parts[parts.length - 1];
};

const DEFAULT_DATE_RANGE = 30;
/**
 * getDownloadDateRangeAndAlertID
 * @param alertDate
 * @param referenceDate
 * @param defaultDays
 * @returns {{fromDate: string, toDate: string, alertID: string}}
 */
export const getDownloadDateRangeAndAlertID = (
  alertDate,
  referenceDate,
  defaultDays = DEFAULT_DATE_RANGE
) => {
  const formattedFromDate = moment(
    referenceDate || alertDate,
    dateFormat_DD_MM_YY
  );
  const formattedToDate = moment(formattedFromDate, dateFormat_DD_MM_YY)
    .subtract(Number(defaultDays), 'days')
    .add(1, 'days');
  return {
    alertID: getAlertIdFromURL(),
    fromDate: formattedFromDate,
    toDate: formattedToDate
  };
};

/**
 * getCodesFromLC
 * @param codeType
 * @returns {Promise<*[]|any>}
 */
export const getCodesFromLC = async (codeType) => {
  try {
    if (window.localStorage.getItem(codeType)) {
      return JSON.parse(window.localStorage.getItem(codeType));
    } else {
      return [];
    }
  } catch (e) {
    return [];
  }
};

export const setCodesJSONToLC = async (codeType, codesJSON) => {
  try {
    window.localStorage.setItem(codeType, JSON.stringify(codesJSON));
  } catch (e) {
    return true;
  }
};

/**
 * getCustomerHighestLabel
 * @param stringOfDataHere
 * @returns {string}
 */
export const getCustomerHighestLabel = (stringOfDataHere) => {
  if (
    stringOfDataHere &&
    stringOfDataHere.trim() !== '' &&
    stringOfDataHere.trim() !== '-'
  ) {
    const listOfData = String(stringOfDataHere).split(',');
    if (listOfData.length > 0) {
      let highestDataValue = 0;
      const highestDataItems = [];

      // fetch the highest percentage value
      listOfData.forEach((dataItem) => {
        // get value contained in parenthesis
        const regExp = /\(([^)]+)\)/;
        const itemPercentageMatches = regExp.exec(dataItem);

        // if it matches anything
        if (itemPercentageMatches && itemPercentageMatches.length > 0) {
          const percentage = itemPercentageMatches[0]
            .replace('%', '')
            .replace('(', '')
            .replace(')', '');

          if (parseFloat(percentage) > highestDataValue) {
            highestDataValue = parseFloat(percentage);
          }
        }
      });

      // next, fetch the data that match the highest percentage value
      listOfData.forEach((dataItem) => {
        // get value contained in parenthesis
        const regExp = /\(([^)]+)\)/;
        const dataItemPercentageMatches = regExp.exec(dataItem);

        // if it matches anything
        if (dataItemPercentageMatches && dataItemPercentageMatches.length > 0) {
          const percentage = dataItemPercentageMatches[0]
            .replace('(', '')
            .replace(')', '');

          if (parseFloat(percentage) === highestDataValue) {
            let searchIndex = dataItem.indexOf('(');
            let stringToTrim = dataItem.substr(searchIndex, dataItem.length);

            const dataItemName = dataItem.replace(stringToTrim, '');
            highestDataItems.push(dataItemName);
          }
        }
      });

      // pour any results within the formattedSegments string outside of this scope
      if (highestDataItems && highestDataItems.length > 0) {
        // highestDataItems.forEach((segmentName, segmentNameIndex) => {
        //   if (segmentNameIndex === highestDataItems.length - 1) {
        //     formattedCustomerSegment += `${segmentName}`;
        //     return;
        //   }

        //   formattedCustomerSegment += `${segmentName}, `;
        //   return;
        // });

        // as per Komal, we just choose one out of many segments if there are multiple high value segments, soooo get the first one
        return `${highestDataItems[0]}`;
      }
    }
  }

  return '-';
};

/**
 * getUserNameFromLC
 * @returns {string}
 */
export const getUserNameFromLC = () => {
  const user_name = window.localStorage.getItem('userName');
  if (user_name) {
    return user_name;
  } else {
    return '';
  }
};

/**
 * getAlertIDFromURL
 * @returns {{alertID: string}}
 */
export const getAlertIDFromURL = () => {
  const href = location.pathname;
  const parts = href.split('/');
  const alertID = parts[parts.length - 1];
  return { alertID };
};

export const getCustomerIDFromURL = () => {
  const href = location.pathname;
  const parts = href.split('/');
  const customerKey = parts[parts.length - 1];
  return {
    customerKey
  };
};

export const getDataFromServer = async (url, headers, params = {}) => {
  const networkInstance = createAxiosServiceInstance();
  console.log('networkInstance,url,headers');
  console.log(networkInstance, url, headers);
  return await networkInstance
    .get(url, { headers, params })
    .then((res) => {
      console.log('RESPONSE', res);
      return res.data;
    })
    .catch((err) => {
      // then print response status
      console.log('ERROR', err);
      return err;
    });
};

export const putDataToServer = async (url, headers, params = {}, data = {}) => {
  const networkInstance = createAxiosServiceInstance();
  return await networkInstance
    .put(url, data, { headers, ...params })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      return err;
    });
};

export const postDataToServer = async (url, data) => {
  const networkInstance = createAxiosServiceInstance();
  return await networkInstance
    .post(url, data)
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      // then print response status
      return err;
    });
};

export const postDataToServerWithFullResponse = async (url, data = {}) => {
  const networkInstance = createAxiosServiceInstance();
  return await networkInstance
    .post(url, data)
    .then((res) => {
      return res;
    })
    .catch((err) => {
      // then print response status
      return err;
    });
};

export const deleteDataFromServer = async (url, data) => {
  const networkInstance = createAxiosServiceInstance();
  return await networkInstance
    .delete(url, {
      data: {
        ...data
      }
    })
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      // then print response status
      return err;
    });
};

export const deleteDataFromServerWithFullResponse = async (url, data) => {
  const networkInstance = createAxiosServiceInstance();
  return await networkInstance
    .delete(url, {
      data: {
        ...data
      }
    })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      // then print response status
      return err;
    });
};

export async function uploadFileToServer(
  url,
  data,
  uploadProgress,
  timeoutInSeconds
) {
  let timeoutInMs = null;
  if (timeoutInSeconds) {
    timeoutInMs = timeoutInSeconds * 1000;
  }

  const networkInstance = createAxiosServiceInstance(null, null, timeoutInMs);
  return await networkInstance
    .post(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (ProgressEvent) => {
        uploadProgress(ProgressEvent);
      }
    })
    .then((res) => {
      // then print response status
      return res;
    })
    .catch((err) => {
      // then print response status
      return err;
    });
}

export const getDataFromResponse = (
  response = {},
  expectedType,
  expectedAttribute = 'data'
) => {
  const data = response[expectedAttribute] || {};
  if (!isEmpty(data)) {
    if (expectedType === 'object') {
      return isEmpty(data) ? {} : data;
    }
    if (expectedType === 'array') {
      return Array.isArray(data) ? data : [];
    }
  } else {
    if (expectedType === 'object') {
      return {};
    }
    if (expectedType === 'array') {
      return [];
    }
  }
};

export const getCustomerKeyFromURL = () => {
  const url = location.pathname;
  const locationSplitArray = url.split('/');
  const locationArray = locationSplitArray.length;
  return locationSplitArray[locationArray - 1];
};

export const getAgeInYears = (dobOrIncorporationDate) => {
  if (!dobOrIncorporationDate) {
    return '';
  }
  try {
    return `${moment().diff(
      moment(
        moment
          .utc(dobOrIncorporationDate, [dateTimeFormat])
          .local()
          .format('DD-MM-YYYY'),
        'DD-MM-YYYY'
      ),
      'years'
    )}`;
  } catch (e) {
    return '';
  }
};

/**
 * This is used when we are downloading stuff and can be extended
 * on basis of download headers like formatNumeric
 * @param responseArray
 * @param responseHeaders
 * @returns {*}
 */
export const responseFormatter = (responseArray, responseHeaders) => {
  responseArray.map((responseObject) => {
    responseHeaders.forEach((headerObject) => {
      const { formatNumeric = false, key } = headerObject;
      if (formatNumeric) {
        responseObject[key] = convertToFormattedMoney(responseObject[key]);
      }
    });
  });
  return responseArray;
};

/**
 * checkIfBoolean
 * @param value
 * @param isAliasRequired
 * @param trueAlias
 * @param falseAlias
 * @returns {string|*}
 * CASES TESTED
 checkIfBoolean("Random Text",true,"YES","NO")
 "NO"
 checkIfBoolean("-",true,"YES","NO")
 "NO"
 checkIfBoolean("",true,"YES","NO")
 "NO"
 checkIfBoolean("false",true,"YES","NO")
 "NO"
 checkIfBoolean("true-this",true,"YES","NO")
 "NO"
 checkIfBoolean("true",true,"YES","NO")
 "YES"
 checkIfBoolean(true,true,"YES","NO")
 "YES"
 checkIfBoolean(false,true,"YES","NO")
 "NO"
 checkIfBoolean(false,true)
 "False"
 checkIfBoolean(true)
 true
 checkIfBoolean(false)
 false
 */
export const checkIfBoolean = (
  value,
  isAliasRequired = false,
  trueAlias = 'True',
  falseAlias = 'False'
) => {
  let actualBool;
  if (isBoolean(value)) {
    actualBool = value;
  } else {
    actualBool = value === 'true';
  }

  if (isAliasRequired) {
    return actualBool ? trueAlias : falseAlias;
  } else {
    return actualBool;
  }
};

//XMLHttpRequest to call Elastic Search API
export const XMLHttpRequestFunc = async (url, data) => {
  //Create a new promise to handle the http request
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    let response = {};
    // 2. Configure it: GET-request for the URL /article/.../load
    xhr.open('POST', url, true);
    // 3. Send the request over the network
    xhr.setRequestHeader('Content-type', 'application/json; charset=utf-8');
    xhr.send(data);
    // 4. This will be called after the response is received
    xhr.onload = function () {
      if (xhr.status != 200) {
        // analyze HTTP status of the response
        response = { status: xhr?.status, statusMsg: xhr.statusText };
        xhr.getResponseHeader('Content-Type');
        alert(`Error ${xhr.status}: ${xhr.statusText}`); // e.g. 404: Not Found
        reject(response);
      } else {
        response = JSON.parse(xhr.response);
        resolve(response);
      }
    };
  });
};

/**
 * sortProductsOptions
 * @param availableProducts
 * @returns {any[]} sorted list
 */
export const sortProductsOptions = (availableProducts) =>
  sortBy(sortBy(availableProducts, [(p) => p.moduleCode]), [
    (p) => !p.isDefault
  ]);

//Filtering the CM Alerts role, to avoid it in the products dropdown
export const filterRole = (data) => {
  return data?.filter((module) => module.roleCode !== 'APP_3_ROLE_CM_ALERTS');
};
